import { inject, Injectable } from '@angular/core';
import { FiDialogsService } from '@fi-shared/fi-dialogs/fi-dialogs.service';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { ISection } from '../models/sections-dnd-editor.models';
import { FiSectionsConfigService } from './fi-sections-config.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { isDefined } from '@fi-shared/utils';
import { cloneDeep } from '@fi-shared/utils/clone-deep';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { AppTextService } from '@fi-shared/app-text/app-text.service';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { FiSectionEditDialogComponent } from '../fi-section-edit-dialog/fi-section-edit-dialog.component';

@Injectable()
export class FiSectionsHelperService {
    private fiDialogsService = inject(FiDialogsService);
    private fiContentLanguageSharedService = inject(FiContentLanguageSharedService);
    private fiSectionsConfigService = inject(FiSectionsConfigService);
    private appTextService = inject(AppTextService);

    openSectionEditor(
        objectType: ObjectTypes,
        selectedLanguageId: number,
        isNewSection: boolean,
        section: ISection,
    ): IFiDialogResult<IContentLanguageLabelModel[]> {
        const config = this.fiSectionsConfigService.getDndEditorConfig(objectType);

        this.fiContentLanguageSharedService.initializeContentLanguagesArray(section.Names, section.Names);
        this.fiContentLanguageSharedService.initializeContentLanguagesArray(section.Descriptions, section.Descriptions);

        return this.fiDialogsService.custom({
            contentComponentRef: FiSectionEditDialogComponent,
            contentComponentData: {
                sectionNames: cloneDeep(section.Names),
                selectedLanguageId: selectedLanguageId,
                isSectionNameRequired:
                    section.Names.filter((name: IContentLanguageLabelModel) => !!name.Value).length < 2,
            },
            hideDialogTopBorder: true,
            preventUppercaseButtonLabel: true,
            headerText: this.appTextService.get(
                isNewSection ? config.sectionNewDialogHeaderTextId : config.sectionEditDialogHeaderTextId,
            ),
            yesButtonText: this.appTextService.get('done'),
            config: {
                panelClass: config.sectionDialogPanelClasses || ['fi-section-editor-panel', 'fi-rounded-dialog-panel'],
                closeOnNavigation: false,
            },
        });
    }

    setDefaultSectionName<T extends { Names: IContentLanguageLabelModel[] }>(
        section: T,
        selectedLanguageId: number,
        objectType: ObjectTypes,
    ): T {
        if (section.Names.some((sectionName: IContentLanguageLabelModel) => sectionName.Value)) {
            return section;
        }

        const selectedTranslationIndex: number = section.Names.findIndex((sectionName: IContentLanguageLabelModel) => {
            return sectionName.ContentLanguageId === selectedLanguageId;
        });
        if (selectedTranslationIndex !== -1) {
            const config = this.fiSectionsConfigService.getDndEditorConfig(objectType);
            section.Names[selectedTranslationIndex].Value = this.appTextService.get(config.sectionPlaceholder);
        }

        return section;
    }
}
